import { useEffect, useState } from "react";

/**
 * A media query as React state.
 *
 * For the cases CSS cannot answer on its own: not rendering something at all,
 * rather than hiding it. `hidden sm:block` still downloads the video and still
 * lets react-joyride position against an off-screen target — the element is in
 * the DOM either way. This keeps it out.
 *
 * Prefer a `lg:` class wherever hiding is enough. Reach for this only when the
 * element's mere existence is the problem.
 */
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const list = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);

    // Read once on mount too: the query may have changed between the initial
    // state and this effect, and on rotation the first event can be missed.
    setMatches(list.matches);
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);

  return matches;
};

/** Tailwind's `lg` — the width at which this app shows its desktop layout. */
export const useIsDesktop = () => useMediaQuery("(min-width: 1024px)");

export default useMediaQuery;
